import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Tabs, Tab, Alert, Modal, Table, Badge } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import AdminNavbar from '../components/AdminNavbar';
import FacilitatorNavbar from '../components/FacilitatorNavbar';
import QANavbar from '../components/QANavbar';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { collection, query, where, getDocs, deleteDoc, doc, addDoc, Timestamp } from 'firebase/firestore';
import { UI_TIMEOUTS } from '../constants/ui';

const SALES_COACHING_TEMPLATES = [
  {
    key: 'encouragement',
    label: 'Encouragement',
    message:
      "Hi {name}, just a reminder that sales coaching starts next week. Come ready to learn, ask questions, and push yourself. Small daily actions add up. You've got this!"
  },
  {
    key: 'thinking-prompt',
    label: 'Thinking Prompt',
    message:
      "Hi {name}, before our next sales coaching session, think about this: What is the biggest reason a customer says no, and how would you respond confidently? Bring your answer to class."
  },
  {
    key: 'link-share',
    label: 'Link Share',
    message:
      "Hi {name}, please use this link before our sales coaching session: {link}\n\nComplete it before class and keep a screenshot/confirmation if needed."
  },
  {
    key: 'attendance-reminder',
    label: 'Session Reminder',
    message:
      "Hi {name}, friendly reminder about your sales coaching session tomorrow. Please be on time and ready to participate. Consistency will help you improve your results."
  },
  {
    key: 'follow-up',
    label: 'Follow-up / Action',
    message:
      "Hi {name}, thank you for attending sales coaching. Your action for today: apply one technique from the session and note what worked. We’ll discuss progress in the next session."
  }
];

const SALES_MOMENTUM_TEMPLATES = [
  {
    key: 'sales-upload-reminder',
    label: 'Sales Upload Reminder',
    message:
      "Hi {name}, please remember to upload your sales for today/this week on the LMS. Keep your figures up to date so your progress and leaderboard ranking are accurate."
  },
  {
    key: 'leaderboard-motivation',
    label: 'Leaderboard Motivation',
    message:
      "Hi {name}, the leaderboard is now visible across all groups. This is your chance to push your ranking higher. Focus on consistency, effort, and smart selling this week!"
  },
  {
    key: 'final-week-push',
    label: 'Final Week Push',
    message:
      "Hi {name}, we’re in the final stretch. Give this week your best effort and complete all required tasks, uploads, and follow-ups. Finish strong — you can do this!"
  },
  {
    key: 'resource-link-share',
    label: 'Resource / Link Share',
    message:
      "Hi {name}, here is an important resource/link for your sales coaching prep: {link}\n\nPlease review it before the next session."
  },
  {
    key: 'missed-session-followup',
    label: 'Missed Session Follow-up',
    message:
      "Hi {name}, we missed you in the last sales coaching session. Please catch up on the shared notes/resources and let us know if you need support before the next class."
  }
];

const WhatsAppComposer = () => {
  const { currentUser, userProfile } = useAuth();
  const functions = getFunctions();

  // State management
  const [whatsappData, setWhatsappData] = useState({
    recipientType: 'program',
    selectedProgram: '',
    specificNumbers: '',
    message: '',
    isScheduled: false,
    scheduledDate: '',
    scheduledTime: '',
    isRecurring: false,
    recurringFrequency: 'weekly',
    recurringEndDate: ''
  });

  const [programs, setPrograms] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [csvRecipients, setCsvRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showScheduledModal, setShowScheduledModal] = useState(false);
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('');
  const [lastSendResults, setLastSendResults] = useState([]);

  // Load programs on mount
  useEffect(() => {
    loadPrograms();
  }, []);

  /**
   * Load facilitator's accessible programs
   */
  const loadPrograms = async () => {
    try {
      const getProgramsFunction = httpsCallable(functions, 'getFacilitatorProgramsForWhatsApp');
      const result = await getProgramsFunction();
      setPrograms(result.data.programs || []);
    } catch (error) {
      devError('Error loading programs:', error);
      setError('Failed to load programs');
    }
  };

  const renderNavbar = () => {
    switch (userProfile?.role) {
      case 'admin':
        return <AdminNavbar currentPage="whatsapp-composer" />;
      case 'qa':
        return <QANavbar />;
      default:
        return <FacilitatorNavbar currentPage="whatsapp-composer" />;
    }
  };

  /**
   * Handle CSV file upload
   */
  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setCsvFile(file);

    // Read and parse CSV
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;

      // Simple CSV parsing (for production, use papaparse library)
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());

      const recipients = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;

        const values = lines[i].split(',').map(v => v.trim());
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index];
        });

        const number = row.phoneNumber || row.phone || row.number;
        if (number) {
          recipients.push({
            number,
            name: row.displayName || row.name || 'Unknown'
          });
        }
      }

      setCsvRecipients(recipients);
    };
    reader.readAsText(file);
  };

  /**
   * Get recipient count
   */
  const getRecipientCount = () => {
    if (whatsappData.recipientType === 'program') {
      const program = programs.find(p => p.id === whatsappData.selectedProgram);
      return program?.learnerCount || 0;
    } else if (whatsappData.recipientType === 'csv') {
      return csvRecipients.length;
    } else {
      const numbers = whatsappData.specificNumbers.split(',').filter(n => n.trim());
      return numbers.length;
    }
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    if (!whatsappData.message || whatsappData.message.trim() === '') {
      setError('Message is required');
      return false;
    }

    if (whatsappData.message.length > 1600) {
      setError('WhatsApp message cannot exceed 1600 characters');
      return false;
    }

    const recipientCount = getRecipientCount();
    if (recipientCount === 0) {
      setError('At least one recipient is required');
      return false;
    }

    if (whatsappData.recipientType === 'program' && !whatsappData.selectedProgram) {
      setError('Please select a program');
      return false;
    }

    if (whatsappData.isScheduled) {
      if (!whatsappData.scheduledDate || !whatsappData.scheduledTime) {
        setError('Scheduled date and time are required');
        return false;
      }

      const scheduledDateTime = new Date(`${whatsappData.scheduledDate}T${whatsappData.scheduledTime}`);
      if (scheduledDateTime <= new Date()) {
        setError('Scheduled time must be in the future');
        return false;
      }
    }

    return true;
  };

  /**
   * Handle send/schedule
   */
  const handleSend = async () => {
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      if (whatsappData.isScheduled) {
        // Schedule for later
        await scheduleWhatsApp();
      } else {
        // Send immediately
        await sendNow();
      }
    } catch (error) {
      devError('Error:', error);
      setError(error.message || 'Failed to send WhatsApp message');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send WhatsApp immediately
   */
  const sendNow = async () => {
    const sendFunction = httpsCallable(functions, 'sendCustomWhatsApp');

    const data = {
      message: whatsappData.message,
      recipientType: whatsappData.recipientType,
      programId: whatsappData.selectedProgram,
      specificNumbers: whatsappData.specificNumbers.split(',').map(n => n.trim()),
      csvData: csvFile ? await fileToBase64(csvFile) : null
    };

    const result = await sendFunction(data);
    setLastSendResults(result?.data?.results || []);
    setSuccess(
      `WhatsApp request processed. Provider accepted: ${result.data.sent}, Failed: ${result.data.failed}. Note: accepted does not always mean delivered to handset.`
    );

    // Reset form
    resetForm();
  };

  /**
   * Schedule WhatsApp for later
   */
  const scheduleWhatsApp = async () => {
    const scheduledDateTime = new Date(`${whatsappData.scheduledDate}T${whatsappData.scheduledTime}`);

    const scheduleData = {
      message: whatsappData.message,
      recipientType: whatsappData.recipientType,
      programId: whatsappData.selectedProgram || null,
      specificNumbers: whatsappData.specificNumbers ? whatsappData.specificNumbers.split(',').map(n => n.trim()) : [],
      csvData: csvFile ? await fileToBase64(csvFile) : null,
      scheduledFor: Timestamp.fromDate(scheduledDateTime),
      recipientCount: getRecipientCount(),
      status: 'pending',
      isRecurring: whatsappData.isRecurring,
      recurringFrequency: whatsappData.isRecurring ? whatsappData.recurringFrequency : null,
      recurringEndDate: whatsappData.isRecurring && whatsappData.recurringEndDate
        ? Timestamp.fromDate(new Date(whatsappData.recurringEndDate))
        : null,
      createdBy: currentUser.uid,
      createdByName: userProfile.displayName || userProfile.email,
      createdAt: Timestamp.now(),
      lastSentAt: null,
      nextScheduledFor: Timestamp.fromDate(scheduledDateTime)
    };

    // Save to Firestore
    await addDoc(collection(db, 'scheduledWhatsApp'), scheduleData);

    setSuccess(`WhatsApp message scheduled successfully for ${scheduledDateTime.toLocaleString()}`);

    // Reset form
    resetForm();
  };

  /**
   * Load scheduled messages
   */
  const loadScheduledMessages = async () => {
    try {
      const q = query(
        collection(db, 'scheduledWhatsApp'),
        where('createdBy', '==', currentUser.uid),
        where('status', 'in', ['pending', 'processing'])
      );

      const snapshot = await getDocs(q);
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledFor: doc.data().scheduledFor?.toDate()
      }));

      setScheduledMessages(messages);
      setShowScheduledModal(true);
    } catch (error) {
      devError('Error loading scheduled messages:', error);
      setError('Failed to load scheduled messages');
    }
  };

  /**
   * Delete scheduled message
   */
  const handleDeleteScheduled = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled WhatsApp message?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'scheduledWhatsApp', messageId));
      setScheduledMessages(prev => prev.filter(m => m.id !== messageId));
      setSuccess('Scheduled message deleted successfully');
    } catch (error) {
      devError('Error deleting scheduled message:', error);
      setError('Failed to delete scheduled message');
    }
  };

  /**
   * Convert file to base64
   */
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  /**
   * Reset form
   */
  const resetForm = () => {
    setWhatsappData({
      recipientType: 'program',
      selectedProgram: '',
      specificNumbers: '',
      message: '',
      isScheduled: false,
      scheduledDate: '',
      scheduledTime: '',
      isRecurring: false,
      recurringFrequency: 'weekly',
      recurringEndDate: ''
    });
    setCsvFile(null);
    setCsvRecipients([]);
    setSelectedTemplateKey('');
    setLastSendResults([]);
  };

  const applyTemplate = (templateKey) => {
    const template = [...SALES_COACHING_TEMPLATES, ...SALES_MOMENTUM_TEMPLATES].find((t) => t.key === templateKey);
    if (!template) return;
    setSelectedTemplateKey(templateKey);
    setWhatsappData((prev) => ({
      ...prev,
      message: template.message
    }));
    setSuccess(`Loaded template: ${template.label}`);
    setTimeout(() => setSuccess(''), UI_TIMEOUTS.SUCCESS_BANNER_MS);
  };

  return (
    <div>
      {renderNavbar()}
      <Container className="mt-4">
      <h2 className="mb-4">WhatsApp Composer</h2>

      {/* Alerts */}
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
      {lastSendResults.length > 0 && (
        <Card className="mb-3">
          <Card.Header><strong>Last Send Diagnostic</strong></Card.Header>
          <Card.Body className="p-0">
            <Table striped bordered hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Provider Status</th>
                  <th>Result</th>
                  <th>Error</th>
                  <th>Message ID</th>
                </tr>
              </thead>
              <tbody>
                {lastSendResults.map((item, index) => (
                  <tr key={`${item.number || 'unknown'}-${index}`}>
                    <td>{item.number || 'N/A'}</td>
                    <td>{item.providerStatus || 'unknown'}</td>
                    <td>{item.success ? 'accepted' : 'failed'}</td>
                    <td>{item.error || '-'}</td>
                    <td>{item.sid || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body>
          {/* Recipient Selection Tabs */}
          <Tabs
            defaultActiveKey="program"
            className="mb-3"
            onSelect={(key) => setWhatsappData({...whatsappData, recipientType: key})}
          >
            <Tab eventKey="program" title="Program Recipients">
              <Form.Group className="mb-3">
                <Form.Label>Select Program</Form.Label>
                <Form.Select
                  value={whatsappData.selectedProgram}
                  onChange={(e) => setWhatsappData({...whatsappData, selectedProgram: e.target.value})}
                >
                  <option value="">Select a program...</option>
                  {programs.map(program => (
                    <option key={program.id} value={program.id}>
                      {program.name} ({program.learnerCount || 0} learners)
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Tab>

            <Tab eventKey="csv" title="CSV Upload">
              <Form.Group className="mb-3">
                <Form.Label>Upload CSV File</Form.Label>
                <Form.Control type="file" accept=".csv" onChange={handleCsvUpload} />
                <Form.Text>
                  CSV should have columns: phoneNumber, displayName (optional)
                </Form.Text>
                {csvRecipients.length > 0 && (
                  <Alert variant="info" className="mt-2">
                    <strong>{csvRecipients.length} recipients found</strong>
                  </Alert>
                )}
              </Form.Group>
            </Tab>

            <Tab eventKey="specific" title="Specific Numbers">
              <Form.Group className="mb-3">
                <Form.Label>Phone Numbers (comma-separated)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="0762683211, +27821234567, 0831234567"
                  value={whatsappData.specificNumbers}
                  onChange={(e) => setWhatsappData({...whatsappData, specificNumbers: e.target.value})}
                />
                <Form.Text>
                  Enter phone numbers separated by commas. Can use formats: 076XXX, +2776XXX, etc.
                </Form.Text>
              </Form.Group>
            </Tab>
          </Tabs>

          {/* Message Composition */}
          <Card className="mb-3 border-info">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <h6 className="mb-0">Sales Coaching Message Templates</h6>
                <Badge bg="info">Quick Fill</Badge>
              </div>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">Choose a template</Form.Label>
                <Form.Select
                  value={selectedTemplateKey}
                  onChange={(e) => applyTemplate(e.target.value)}
                >
                  <option value="">Select a template...</option>
                  {SALES_COACHING_TEMPLATES.map((template) => (
                    <option key={template.key} value={template.key}>
                      {template.label}
                    </option>
                  ))}
                  <optgroup label="Sales Uploads / Leaderboard / Final Push">
                    {SALES_MOMENTUM_TEMPLATES.map((template) => (
                      <option key={template.key} value={template.key}>
                        {template.label}
                      </option>
                    ))}
                  </optgroup>
                </Form.Select>
              </Form.Group>
              <div className="mb-2">
                <small className="text-muted">Sales coaching templates</small>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                {SALES_COACHING_TEMPLATES.map((template) => (
                  <Button
                    key={template.key}
                    size="sm"
                    variant={selectedTemplateKey === template.key ? 'primary' : 'outline-primary'}
                    onClick={() => applyTemplate(template.key)}
                  >
                    {template.label}
                  </Button>
                ))}
              </div>
              <div className="mt-3 mb-2">
                <small className="text-muted">Sales uploads / leaderboard / final week templates</small>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                {SALES_MOMENTUM_TEMPLATES.map((template) => (
                  <Button
                    key={template.key}
                    size="sm"
                    variant={selectedTemplateKey === template.key ? 'success' : 'outline-success'}
                    onClick={() => applyTemplate(template.key)}
                  >
                    {template.label}
                  </Button>
                ))}
              </div>
              <Form.Text className="d-block mt-2">
                Use <code>{'{name}'}</code> for personalization and <code>{'{link}'}</code> when sharing a session/form link.
              </Form.Text>
            </Card.Body>
          </Card>

          <Form.Group className="mb-3">
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              placeholder="Type your message here... Use {name} for personalization"
              value={whatsappData.message}
              onChange={(e) => setWhatsappData({...whatsappData, message: e.target.value})}
              maxLength={1600}
            />
            <Form.Text>
              {whatsappData.message.length}/1600 characters
              {whatsappData.message.includes('{name}') && ' | Personalization enabled'}
            </Form.Text>
          </Form.Group>

          {/* Scheduling Options */}
          <Card className="mb-3 bg-light">
            <Card.Body>
              <h6>Scheduling Options</h6>

              <Form.Check
                type="checkbox"
                label="Schedule for later"
                checked={whatsappData.isScheduled}
                onChange={(e) => setWhatsappData({...whatsappData, isScheduled: e.target.checked})}
                className="mb-3"
              />

              {whatsappData.isScheduled && (
                <>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={whatsappData.scheduledDate}
                          onChange={(e) => setWhatsappData({...whatsappData, scheduledDate: e.target.value})}
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label>Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={whatsappData.scheduledTime}
                          onChange={(e) => setWhatsappData({...whatsappData, scheduledTime: e.target.value})}
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <Form.Check
                    type="checkbox"
                    label="Make this recurring"
                    checked={whatsappData.isRecurring}
                    onChange={(e) => setWhatsappData({...whatsappData, isRecurring: e.target.checked})}
                    className="mb-3"
                  />

                  {whatsappData.isRecurring && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Frequency</Form.Label>
                        <Form.Select
                          value={whatsappData.recurringFrequency}
                          onChange={(e) => setWhatsappData({...whatsappData, recurringFrequency: e.target.value})}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>End Date (optional)</Form.Label>
                        <Form.Control
                          type="date"
                          value={whatsappData.recurringEndDate}
                          onChange={(e) => setWhatsappData({...whatsappData, recurringEndDate: e.target.value})}
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <Form.Text>Leave empty for indefinite recurrence</Form.Text>
                      </Form.Group>

                      <Alert variant="warning">
                        This message will be sent {whatsappData.recurringFrequency} starting on {whatsappData.scheduledDate}
                        {whatsappData.recurringEndDate ? ` until ${whatsappData.recurringEndDate}` : ' indefinitely'}.
                      </Alert>
                    </>
                  )}
                </>
              )}
            </Card.Body>
          </Card>

          {/* Summary */}
          <Card className="mb-3">
            <Card.Body>
              <h6>Message Summary</h6>
              <p className="mb-1"><strong>Recipients:</strong> {getRecipientCount()}</p>
              <p className="mb-1"><strong>Message Length:</strong> {whatsappData.message.length} characters</p>
              {whatsappData.isScheduled && (
                <p className="mb-1">
                  <strong>Scheduled:</strong> {whatsappData.scheduledDate} at {whatsappData.scheduledTime}
                  {whatsappData.isRecurring && ` (${whatsappData.recurringFrequency})`}
                </p>
              )}
            </Card.Body>
          </Card>

          {/* Action Buttons */}
          <div className="d-flex gap-2 justify-content-between">
            <Button variant="outline-secondary" onClick={loadScheduledMessages}>
              View Scheduled Messages
            </Button>

            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={resetForm}>
                Reset
              </Button>
              <Button
                variant="primary"
                onClick={handleSend}
                disabled={loading}
              >
                {loading ? 'Sending...' : (whatsappData.isScheduled ? 'Schedule WhatsApp' : 'Send Now')}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Scheduled Messages Modal */}
      <Modal show={showScheduledModal} onHide={() => setShowScheduledModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Scheduled WhatsApp Messages</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {scheduledMessages.length === 0 ? (
            <Alert variant="info">No scheduled messages found.</Alert>
          ) : (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Message</th>
                  <th>Recipients</th>
                  <th>Scheduled For</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {scheduledMessages.map(message => (
                  <tr key={message.id}>
                    <td>{message.message.substring(0, 50)}...</td>
                    <td>{message.recipientCount}</td>
                    <td>{message.scheduledFor?.toLocaleString()}</td>
                    <td>
                      <Badge bg={message.status === 'pending' ? 'warning' : 'info'}>
                        {message.status}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteScheduled(message.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowScheduledModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>
    </div>
  );
};

export default WhatsAppComposer;
